import React from 'react';
import { PanelWrapper, PickerPanelSC } from './picker-panel.styled';
import { PickerPanelProps as OriginalProps } from 'rc-picker';
import { useSelector } from 'react-redux';
import TStore from '../../../../types/store';
import { ReactComponent as SuperNextIcon } from '../../../../assets/svg/pickers/picker_super_next.svg';
import { ReactComponent as NextIcon } from '../../../../assets/svg/pickers/picker_next.svg';
import { ReactComponent as SuperPrevIcon } from '../../../../assets/svg/pickers/picker_super_prev.svg';
import { ReactComponent as PrevIcon } from '../../../../assets/svg/pickers/picker_prev.svg';

import enUS from 'rc-picker/lib/locale/en_US';
import skSK from 'rc-picker/lib/locale/sk_SK';
import csCZ from 'rc-picker/lib/locale/cs_CZ';
import generateFns from 'rc-picker/lib/generate/dateFns';
import generateMoment from 'rc-picker/lib/generate/moment';
import Portal from '../../portal';
import useResizeObserver from 'use-resize-observer';
import rafSchd from 'raf-schd';

type TPickerType = <T extends any>(props: DateTimePickerProps<T>) => JSX.Element;

type TO_OMIT =
  | 'nextIcon'
  | 'superNextIcon'
  | 'prevIcon'
  | 'superPrevIcon'
  | 'locale'
  | 'generateConfig';

type DateTimePickerProps<T> = Omit<OriginalProps<T>, TO_OMIT> & {
  visible: boolean;
  parentRef: React.RefObject<Element | null>;
  onClose?: () => void;
  locale?: 'sk' | 'en' | 'cs';
  generateConfig: 'moment' | 'dateFns';
};

const PickerPanel = <T extends any>({
  locale,
  generateConfig = 'moment',
  visible,
  style,
  parentRef,
  onClose,
  ...rest
}: DateTimePickerProps<T>) => {
  const lang = useSelector((state: TStore) => locale || state.app.currentLang);
  const ref = React.useRef<any>(null);
  const [menuPosition, setMenuPosition] = React.useState<DOMRect | undefined>(undefined);

  const handleMenuPos = React.useCallback(() => {
    if (visible) {
      const newPosition = parentRef?.current?.getBoundingClientRect();
      setMenuPosition((o) => (newPosition || o) as DOMRect);
    }
  }, [visible, parentRef]);

  React.useLayoutEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      // @ts-ignore
      if (
        ref.current &&
        // @ts-ignore
        !ref.current?.contains(event.target) &&
        // @ts-ignore
        !parentRef?.current?.contains(event.target)
      ) {
        onClose?.();
      }
    }

    if (visible) {
      handleMenuPos();
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [visible, onClose, handleMenuPos]);

  React.useLayoutEffect(() => {
    const callback = rafSchd(handleMenuPos);
    const node = parentRef?.current;

    if (visible) {
      node?.addEventListener('scroll', callback);
      window.addEventListener('scroll', callback);
    }

    return () => {
      window.removeEventListener('scroll', callback);
      node?.removeEventListener('scroll', callback);
    };
  }, [visible, handleMenuPos, parentRef]);

  React.useLayoutEffect(() => {
    function handleEsc(event: React.KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose?.();
      }
    }
    if (visible) {
      // @ts-ignore
      document.addEventListener('keydown', handleEsc, false);
    }
    return () => {
      // @ts-ignore
      document.removeEventListener('keydown', handleEsc, false);
    };
  }, [onClose, visible]);

  const langFile = React.useMemo(() => {
    switch (lang) {
      case 'cs':
        return csCZ;
      case 'en':
        return enUS;
      default:
        return skSK;
    }
  }, [lang]);

  useResizeObserver({
    onResize: handleMenuPos,
    // @ts-ignore
    ref: parentRef,
  });

  return (
    <Portal>
      {visible && (
        <PanelWrapper
          style={
            menuPosition
              ? {
                  ...style,
                  position: 'fixed',
                  top: menuPosition.bottom + 10,
                  left: `calc(${menuPosition.left + menuPosition.width / 2}px - 140px)`,
                }
              : style
          }
          ref={ref}
        >
          <PickerPanelSC
            {...rest}
            locale={langFile}
            generateConfig={(generateConfig === 'moment' ? generateMoment : generateFns) as any}
            // @ts-ignore
            nextIcon={<NextIcon />}
            superNextIcon={<SuperNextIcon />}
            prevIcon={<PrevIcon />}
            superPrevIcon={<SuperPrevIcon />}
          />
        </PanelWrapper>
      )}
    </Portal>
  );
};

export default PickerPanel as TPickerType;
